import React, { useState } from 'react'
import { NavLink, useParams } from 'react-router-dom'
import { Breadcrumb, Col, Container, Row, Spinner, Tab } from 'react-bootstrap'
import { formatDate, getUserDisplayName } from '../../apps/professional-app/fa/common/aifeatures/utils'
import { selectAccountId, selectAuthenticatedUser } from '../../store/auth/authSlice'
import { UserAvatar } from '../../components/UserAvatar/UserAvatar'
import { useUserAvatarProps } from '../../components/UserAvatar/useUserAvatarProps'
import { useGetUserByIdQuery } from '../../store/user/user'
import { useAppSelector } from '../../store/hooks'
import buttonStyles from '../../components/Button/Button.module.scss'
import { Roles } from '../../components/Roles/Roles'
import './User.scss'
import { EditUserModal } from './EditUserModal'
import { Tabs } from '../../components/Tabs/Tabs'
import { UserTasks } from './UserTasks'
import { useUserAnalyticsTabs } from './hooks'

import { NoQuotaModalBody, NoQuotaModalFooter, NoQuotaModalHeader } from '../Analytics/NoQuotaModal'
import DashboardView from '../Analytics/DashboardView'
import { NotAuthorizedPageContent } from '../../components/NotAuthorizedPageContent'
import { isAdmin, isOwner } from '../../common/userUtils'
import { parseUserMetadata } from '../../components/Consent/model'
import FormattedDate from '../../components/FormattedDate'
import { useUserScoresData } from '../../components/Insights/useUserScoresData'
import { hasEnoughMeetingsForScore } from '../../components/Insights/scoreRoster'
import { useZocksQuota } from '../../components/ScopedBlock/hooks'
import { UserScoreStat } from '../../components/Insights/UserMeetingScoreStat/UserScoreStat'
import { type RangeKey } from '../../components/Insights/userScoreRange'

const MEETING_SCORE_RANGE: RangeKey = '90d'

export const UserView: React.FC = () => {
  const accountId = useAppSelector(selectAccountId)
  const authenticatedUser = useAppSelector(selectAuthenticatedUser)

  const { userId = '', modal = null, activeTab = 'tasks' } = useParams()

  const isNotAuthorized = userId !== authenticatedUser?.id && !isAdmin(authenticatedUser) && !isOwner(authenticatedUser)

  const avatarProps = useUserAvatarProps({ userId: isNotAuthorized ? undefined : userId, width: '72px', height: '72px', fontSize: '40px' })
  const { data: userData, isFetching: isFetchingUserData } = useGetUserByIdQuery(
    { accountId: accountId, userId: userId },
    { refetchOnMountOrArgChange: true, skip: !userId || isNotAuthorized },
  )
  const { hasQuota } = useZocksQuota()
  const insightsVisible = hasQuota('insights.enabled.value')
  const { scorecards, isFetching: isFetchingScore } = useUserScoresData(accountId, MEETING_SCORE_RANGE, {
    enabled: !isNotAuthorized && insightsVisible,
    userIds: [userId],
  })
  const scorecard = scorecards.find((row) => row.user_id === userId)
  const meetingScore = hasEnoughMeetingsForScore(scorecard?.meetings_current ?? 0, scorecard?.avg_score_current) ? (scorecard?.avg_score_current ?? null) : null
  const meetingScoreDelta = meetingScore === null ? null : (scorecard?.avg_score_trend ?? null)
  const [showEditContactModal, setShowEditUserModal] = useState<boolean>(modal === 'edit')

  const [selectedTab, setSelectedTab] = useState<string>(activeTab)

  const { dashboards, dashboardAvailable, userHasAccess: userHasDashboardAccess, isLoading: dashboardsIsLoading } = useUserAnalyticsTabs({ userId })

  if (isNotAuthorized) {
    return <NotAuthorizedPageContent />
  }

  const handleCancelUserModal = () => {
    setShowEditUserModal(false)
  }

  const changeTab = (key: string | null) => {
    if (key) {
      setSelectedTab(key)
    }
  }

  if (isFetchingUserData || !userData?.data) {
    return (
      <div className='d-flex align-items-center justify-content-center'>
        <Spinner />
      </div>
    )
  }

  const displayName =
    userData.data.status === 'deactivated' ? getUserDisplayName(userData.data).replace(/\s\(deactivated\)$/, '') : getUserDisplayName(userData.data)

  const userMetadata = parseUserMetadata(userData.data)
  const deactivatedAt = userData.data.status === 'deactivated' && 'deactivatedAt' in userMetadata && userMetadata['deactivatedAt']

  const getUserAnalyticsTabs = () => {
    const NoDashboardTab = (
      <Tab key='noDashboard' eventKey='noDashboard' title='User Analytics'>
        <div className='position-relative'>
          <div className='position-absolute top-0 w-100 pt-5 z-1'>
            <Container>
              <Row>
                <Col xs={12} md={6} className='mx-auto'>
                  <div className='d-flex flex-column gap-4 align-items-center'>
                    {NoQuotaModalHeader}
                    {NoQuotaModalBody}
                    <div className='mt-4'>{NoQuotaModalFooter}</div>
                  </div>
                </Col>
              </Row>
            </Container>
          </div>
          <div className='position-absolute top-0 w-100 h-100  upgrade-info-container bg-white d-flex flex-column justify-content-start align-items-center'></div>
          <img alt='' src='https://static.zocks.io/assets/images/analytics hub_bg_2540_1429.png' className='w-100' />
        </div>
      </Tab>
    )

    if (!userHasDashboardAccess || dashboardsIsLoading || !dashboards?.length) {
      return []
    }

    if (!dashboardAvailable) {
      return [NoDashboardTab]
    }

    return dashboards.map((dashboard) => (
      <Tab key={dashboard.id} eventKey={dashboard.id} title={dashboard.name}>
        <div>
          <DashboardView dashboard={dashboard} initialHeight={1300} urlParams={{ accountId, userId }}></DashboardView>
        </div>
      </Tab>
    ))
  }

  return (
    <Container fluid className='full-size-content'>
      {showEditContactModal ? <EditUserModal userId={userId} onHide={handleCancelUserModal} /> : null}

      <Row>
        <Col className='d-flex flex-column zocks-gap-3'>
          <Row>
            <Col className='p-0 d-flex flex-column zocks-gap-3'>
              <Row>
                <Col className='p-0'>
                  <Breadcrumb>
                    <Breadcrumb.Item
                      linkProps={{
                        to: '/account/manage-team',
                        className: `${buttonStyles.button} ${buttonStyles.linkButton} text-decoration-underline`,
                      }}
                      linkAs={NavLink}
                    >
                      My Team
                    </Breadcrumb.Item>
                    <Breadcrumb.Item active>{displayName}</Breadcrumb.Item>
                  </Breadcrumb>
                </Col>
              </Row>
              <Row>
                <Col className='d-flex flex-column zocks-gap-3'>
                  <Row className='align-self-start'>
                    <Col className='p-0 d-flex align-items-center gap-2'>
                      <UserAvatar {...avatarProps} />
                      <h3>{displayName}</h3>
                    </Col>
                  </Row>
                  <Row className='d-flex zocks-gap-2 justify-content-between'>
                    <Col md={12} lg={9}>
                      <Row className='w-100 h-100 zocks-gap-2'>
                        <Col className='ps-3 p-0 gap-1 d-flex flex-column user-data-container w-100'>
                          <div className='lead-small'>Email</div>
                          <div className='paragraph-medium'>{userData.data.email}</div>
                        </Col>
                        <Col className='ps-3 p-0 gap-1 d-flex flex-column user-data-container w-100'>
                          <div className='lead-small'>Role</div>
                          <div className='paragraph-medium'>
                            <Roles userRoleIds={userData.data.roles.map((role) => role.id)} />
                          </div>
                        </Col>
                        <Col className='ps-3 p-0 gap-1 d-flex flex-column user-data-container w-100'>
                          <div className='lead-small'>Status</div>
                          <div className='paragraph-medium'>
                            <div className='paragraph-medium'>{userData.data.status}</div>
                          </div>
                        </Col>
                        {insightsVisible ? (
                          <Col className='p-0 user-data-container'>
                            <UserScoreStat
                              userId={userId}
                              score={meetingScore}
                              delta={meetingScoreDelta}
                              isFetching={isFetchingScore}
                              range={MEETING_SCORE_RANGE}
                            />
                          </Col>
                        ) : null}
                        <Col className='ps-3 p-0 gap-1 d-flex flex-column user-data-container'>
                          <div className='lead-small'>Date Added</div>
                          <FormattedDate date={userData.data.createdAt ? formatDate(userData.data.createdAt) : ''} />
                        </Col>
                        {deactivatedAt && (
                          <Col className='ps-3 p-0 gap-1 d-flex flex-column user-data-container'>
                            <div className='lead-small'>Date Deactivated</div>
                            <FormattedDate date={deactivatedAt ? formatDate(deactivatedAt) : ''} />
                          </Col>
                        )}
                      </Row>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Col>
          </Row>
          <Row>
            <Col className='p-0 w-100'>
              <Row>
                <Col className='p-0'>
                  <Tabs activeKey={selectedTab} id='user-view-tabs' className='zocks-mb-2.5 mt-4' onSelect={changeTab} mountOnEnter>
                    <Tab eventKey='tasks' title='Tasks'>
                      <UserTasks user={userData.data} />
                    </Tab>
                    {getUserAnalyticsTabs()}
                  </Tabs>
                </Col>
              </Row>
            </Col>
          </Row>
        </Col>
      </Row>
    </Container>
  )
}
